import { Box, Heading, HStack, Text, VStack } from '@gluestack-ui/themed';
import { ArrowLeft, DownloadIcon } from 'lucide-react-native';
import type { FC } from 'react';
import { useContext, useEffect, useState } from 'react';

import Bioeureka from '../assets/bioeureka-primary.svg';
import { PrimaryButton } from '../components/buttons/PrimaryButton';
import { TerciaryButton } from '../components/buttons/TerciaryButton';
import { TranslationContext } from '../contexts/translations/Translation';
import { UserContext } from '../contexts/User';
import type { IAnalysisExportPageProps } from '../routes/Routes';
import { COLORS } from '../theme/colors';
import { pascalToSnake } from '../utils/StringUtils';

// TODO: Refactor this page, it's old and not well structured
export const AnalysisExportPage: FC<IAnalysisExportPageProps> = (props: IAnalysisExportPageProps) => {
  const { navigation } = props;
  const { analysis, reportType, biochemicalResults, neededTests, strain } = props.route.params;
  const { user, currentWorkspace } = useContext(UserContext) ?? {};

  const [image] = useState<string>(analysis.imageUrl ?? '');
  const [showBackArrow, setShowBackArrow] = useState<boolean>(false);

  const { i18n } = useContext(TranslationContext);

  // TODO: Fetch from API
  const workspace = {
    id: i18n?.t('export.form.sample-information-section.laboratory-id') || '',
    name: currentWorkspace?.name,
    email: currentWorkspace?.email,
    phoneNumber: currentWorkspace?.phoneNumber,
    city: 'Saint-Nicolas',
    country: 'Canada',
    postalCode: 'G7A 2P4',
  };
  const TEMPERATURE = analysis.sampleTemperature ? i18n?.t('export.form.sample-information-section.temperature-value', {
    temp: analysis.sampleTemperature,
  }) : '';
  const METHOD_REF = i18n?.t('export.form.sample-information-section.method-reference') || '';
  const SAMPLE_ID = analysis?.sampleId ?? analysis.code;
  const RECEIVED_DATE = new Date(analysis?.receptionDate ?? analysis.createdAt ?? new Date()).toLocaleDateString();
  const AUTHORIZED_BY = user?.firstName + ' ' + user?.lastName;
  // Academic (private) workspaces get a lighter, non-regulatory COA: no manager
  // sign-off / signature line (students aren't a certifying lab).
  const isAcademic = ((currentWorkspace as any)?.visibility) === 'private';

  // Biochemical data: prefer explicit params, fall back to what was persisted in analysis.properties
  const biochemicalResultsToUse: Record<string, string | null> | undefined =
    biochemicalResults ?? (analysis.properties as any)?.biochemicalResults ?? undefined;
  const neededTestsToUse: string[] =
    (neededTests && neededTests.length > 0)
      ? neededTests
      : (biochemicalResultsToUse ? Object.keys(biochemicalResultsToUse) : []);

  // Detection: ensure all selected organisms appear even if score is 0
  const selectedOrganisms: string[] = (() => {
    const raw = (analysis?.properties as any)?.organisms;
    if (!raw) return ['bacteria', 'yeast', 'mold'];
    return typeof raw === 'string' ? raw.split(',') : raw;
  })();

  const detectionResults = analysis?.results as Record<string, number | string> | undefined;
  const useBinaryFormat = !!(detectionResults?.['bacteria_decision'] ?? detectionResults?.['yeast_decision'] ?? detectionResults?.['mold_decision']);

  const ORGANISM_LABELS: Record<string, string> = {
    bacteria: i18n?.t('page.analysis-details.organism-selection.bacteria.label') ?? 'Bactérie',
    yeast: i18n?.t('page.analysis-details.organism-selection.yeast.label') ?? 'Levure',
    mold: i18n?.t('page.analysis-details.organism-selection.mold.label') ?? 'Moisissure',
  };

  // Strain results (all, no slice)
  const strainResults = (analysis as any)?.strainResults as Record<string, number> | undefined;

  // Concordance for top strain
  const allConcordant = neededTestsToUse.length > 0 && neededTestsToUse.every((key) => {
    const entered = biochemicalResultsToUse?.[key];
    const expected = (strain as any)?.[key]?.toString();
    return entered != null && entered === expected;
  });

  // Bilingual hardcoded column headers (no i18n keys exist for these)
  const isEn = i18n?.locale === 'en';
  const COL_EXPECTED = isEn ? 'Expected' : 'Attendu';
  const COL_RESULT = isEn ? 'Result' : 'Résultat';
  const COL_STATUS = isEn ? 'Status' : 'Statut';

  const displayTestValue = (val: string | null | undefined): string => {
    if (val === 'p') return isEn ? 'Positive' : 'Positif';
    if (val === 'm') return isEn ? 'Negative' : 'Négatif';
    return val ?? '—';
  };

  const printPage = () => (window as any).print?.();

  const runPrint = () => {
    setShowBackArrow(false);
    navigation.setOptions({ headerShown: false });
    setTimeout(() => {
      printPage();
      navigation.setOptions({ headerShown: true });
      setShowBackArrow(true);
    }, 300);
  };

  useEffect(() => {
    runPrint();
  }, [image]);

  const SectionHeader = ({ header }: { header: string[] }) => (
    <Box style={{ flexDirection: 'row', marginHorizontal: 10, padding: 5, paddingTop: 20, borderBottomWidth: 1 }}>
      {header.map((item, index) => (
        <Text key={index} style={{ fontWeight: 'bold', width: '50%', fontSize: 24 }}>
          {item}
        </Text>
      ))}
    </Box>
  );

  const SectionHalfAndHalf = ({ fieldName, fieldValue }: { fieldName: string[]; fieldValue: string[] }) => (
    <Box style={{ flexDirection: 'row', marginHorizontal: 10, padding: 5, paddingTop: 5 }}>
      {fieldName?.map((item: string, index) => (
        <Box key={index} style={{ display: 'flex', width: '50%', alignContent: 'flex-start' }}>
          <Text>{item}</Text>
          {fieldValue[index] && <Text style={{ fontWeight: 'bold' }}>{fieldValue[index]}</Text>}
        </Box>
      ))}
    </Box>
  );

  return (
    <>
      {/* Hide nav bar and buttons when printing */}
      <style>{`
        @media print {
          body * { visibility: hidden; }
          #coa-root { visibility: visible; position: absolute; left: 0; top: 0; width: 100%; }
          #coa-root * { visibility: visible; }
          .coa-watermark { -webkit-print-color-adjust: exact; print-color-adjust: exact; }
          .no-print { display: none !important; }
        }
      `}</style>

      <div id="coa-root" style={{ position: 'relative' }}>
        {isAcademic && (
          <div aria-hidden="true" className="coa-watermark" style={{ position: 'absolute', top: 0, left: 0, right: 0, bottom: 0, zIndex: 0, overflow: 'hidden', pointerEvents: 'none' }}>
            <div style={{ position: 'absolute', top: '-20%', left: '-20%', width: '140%', height: '140%', transform: 'rotate(-30deg)', display: 'flex', flexWrap: 'wrap', alignContent: 'flex-start' }}>
              {Array.from({ length: 48 }).map((_, i) => (
                <img key={i} src={Bioeureka} aria-hidden="true" alt="" style={{ width: 180, height: 'auto', opacity: 0.07, filter: 'grayscale(1)', margin: '28px 20px' }} />
              ))}
            </div>
          </div>
        )}
        <VStack style={{ position: 'relative', zIndex: 1 }}>
          {showBackArrow && (
            <HStack className="no-print" justifyContent="space-between">
              <TerciaryButton
                style={{ margin: 10, padding: 0 }}
                leftIcon={() => <ArrowLeft size="16px" strokeWidth="1.25px" color={COLORS.ACCENT[100]} />}
                content={i18n.t('export.go-back')}
                onPress={() => navigation.goBack()}
              />
              <PrimaryButton
                style={{ margin: 20 }}
                leftIcon={() => <DownloadIcon size="16px" strokeWidth="1.25px" style={{ marginRight: 5 }} color={COLORS.NEUTRAL[0]} />}
                content={i18n.t('export.download-again')}
                onPress={() => runPrint()}
              />
            </HStack>
          )}

          <Box style={{ flexDirection: 'row', justifyContent: 'space-between', marginHorizontal: 10, padding: 5 }}>
            {image && <img src={Bioeureka} height={120} width={240} style={{ borderRadius: 10 }} />}
            {image && <img crossOrigin="anonymous" src={image} height={200} width={200} style={{ borderRadius: '20px' }} />}
          </Box>

          <Box style={{ flexDirection: 'row', justifyContent: 'center', marginHorizontal: 10, padding: 5 }}>
            <Heading size="xl">{i18n?.t('export.header.title')}</Heading>
          </Box>

          {/* Client & COA section */}
          <SectionHeader header={[i18n?.t('export.form.client-section.title') || '', i18n?.t('export.form.coa-section.title') || '']} />
          <SectionHalfAndHalf
            fieldName={[i18n?.t('export.form.client-section.client-name') || '', i18n?.t('export.form.coa-section.coa-number') || '']}
            fieldValue={[workspace.name || '', `${analysis.code}`]}
          />
          <SectionHalfAndHalf
            fieldName={[i18n?.t('export.form.client-section.client-email-address') || '', i18n?.t('export.form.coa-section.coa-date') || '']}
            fieldValue={[workspace?.email || '', new Date(analysis.confirmedDate ?? new Date()).toLocaleDateString(i18n.locale)]}
          />
          {workspace.phoneNumber ? (
            <SectionHalfAndHalf
              fieldName={[i18n?.t('export.form.client-section.client-phone') || '']}
              fieldValue={[workspace.phoneNumber]}
            />
          ) : null}

          {/* Sample information */}
          <SectionHeader header={[i18n?.t('export.form.sample-information-section.title') || '']} />
          <SectionHalfAndHalf
            fieldName={[i18n?.t('export.form.sample-information-section.sample-id') || '', i18n?.t('export.form.sample-information-section.sample-received-date') || '']}
            fieldValue={[SAMPLE_ID, RECEIVED_DATE]}
          />
          <SectionHalfAndHalf
            fieldName={[i18n?.t('export.form.sample-information-section.temperature') || '']}
            fieldValue={[TEMPERATURE]}
          />
          <SectionHalfAndHalf
            fieldName={[i18n?.t('export.form.sample-information-section.analysis-date') || '', '']}
            fieldValue={[new Date(analysis.createdAt ?? new Date()).toLocaleDateString(), '']}
          />

          {/* Results section */}
          <SectionHeader header={[i18n?.t('export.form.result-table-section.title') || '']} />
          <Box style={{ marginHorizontal: 10, padding: 5, paddingTop: 5 }}>

            {/* Detection report */}
            {reportType === 'detection' && (
              <>
                <Box style={{ flexDirection: 'row', borderBottomWidth: 1, display: 'flex' }}>
                  <Text style={{ width: '33%', fontWeight: 'bold', fontSize: 'medium' }}>
                    {i18n?.t('export.form.result-table-section.header.pathogen')}
                  </Text>
                  <Text style={{ width: '33%', fontWeight: 'bold', fontSize: 'medium' }}>
                    {i18n?.t('export.form.result-table-section.header.probability')}
                  </Text>
                  <Text style={{ width: '34%', fontWeight: 'bold', fontSize: 'medium' }}>
                    {i18n?.t('page.analysis-details.detection.title')}
                  </Text>
                </Box>
                {selectedOrganisms.map((org) => {
                  if (useBinaryFormat) {
                    const decision = (detectionResults?.[`${org}_decision`] as string) ?? 'INCERTAIN';
                    const proba = (detectionResults?.[`${org}_proba`] as number) ?? 0;
                    const decisionColor = decision === 'PRESENCE' ? COLORS.ACCENT[100] : decision === 'ABSENCE' ? COLORS.STATUS.SUCCESS : '#F59E0B';
                    const decisionLabel = decision === 'PRESENCE'
                      ? i18n?.t('page.analysis-details.detection.decision.presence')
                      : decision === 'ABSENCE'
                        ? i18n?.t('page.analysis-details.detection.decision.absence')
                        : i18n?.t('page.analysis-details.detection.decision.incertain');
                    return (
                      <Box key={org} style={{ flexDirection: 'row' }}>
                        <Text style={{ width: '33%', fontSize: 'medium' }}>{ORGANISM_LABELS[org] ?? org}</Text>
                        <Text style={{ width: '33%', fontSize: 'medium' }}>{(proba * 100).toFixed(1)}%</Text>
                        <Text style={{ width: '34%', fontSize: 'medium', color: decisionColor }}>{decisionLabel ?? decision}</Text>
                      </Box>
                    );
                  }
                  const prob = (detectionResults?.[`${org}_detected`] as number) ?? 0;
                  const detected = prob >= 0.5;
                  return (
                    <Box key={org} style={{ flexDirection: 'row' }}>
                      <Text style={{ width: '33%', fontSize: 'medium' }}>{ORGANISM_LABELS[org] ?? org}</Text>
                      <Text style={{ width: '33%', fontSize: 'medium' }}>{(prob * 100).toFixed(1)}%</Text>
                      <Text style={{ width: '34%', fontSize: 'medium', color: detected ? COLORS.ACCENT[100] : COLORS.STATUS.SUCCESS }}>
                        {detected
                          ? i18n?.t('page.analysis-details.detection.detected')
                          : i18n?.t('page.analysis-details.detection.not-detected')}
                      </Text>
                    </Box>
                  );
                })}

                {/* Gram reaction + morphology (from the 4-head identification) */}
                {(() => {
                  const idLabel = (v?: string) => {
                    if (v === 'pos') return i18n?.t('verrou.hypothesis.gram-pos');
                    if (v === 'neg') return i18n?.t('verrou.hypothesis.gram-neg');
                    if (v === 'cocci') return i18n?.t('verrou.hypothesis.shape-cocci');
                    if (v === 'bacillus') return i18n?.t('verrou.hypothesis.shape-bacillus');
                    return undefined;
                  };
                  const gram = detectionResults?.['gram'] as string | undefined;
                  const shape = detectionResults?.['shape'] as string | undefined;
                  const gramProba = (detectionResults?.['gram_proba'] as number) ?? 0;
                  const shapeProba = (detectionResults?.['shape_proba'] as number) ?? 0;
                  const rows: { label: string; value: string; proba: number }[] = [];
                  if (idLabel(gram)) rows.push({ label: i18n?.t('verrou.hypothesis.gram-label') ?? '', value: idLabel(gram)!, proba: gramProba });
                  if (idLabel(shape)) rows.push({ label: i18n?.t('verrou.hypothesis.shape-label') ?? '', value: idLabel(shape)!, proba: shapeProba });
                  if (!rows.length) return null;
                  return (
                    <Box style={{ marginTop: 16 }}>
                      <Text style={{ fontWeight: 'bold', fontSize: 'medium', marginBottom: 4 }}>Identification</Text>
                      {rows.map((r) => (
                        <Box key={r.label} style={{ flexDirection: 'row' }}>
                          <Text style={{ width: '50%', fontSize: 'medium' }}>{r.label}</Text>
                          <Text style={{ width: '30%', fontSize: 'medium', fontWeight: 'bold' }}>{r.value}</Text>
                          <Text style={{ width: '20%', fontSize: 'medium' }}>{(r.proba * 100).toFixed(0)}%</Text>
                        </Box>
                      ))}
                    </Box>
                  );
                })()}
              </>
            )}

            {/* Strain report */}
            {reportType === 'strain' && (
              <>
                <Box style={{ flexDirection: 'row', borderBottomWidth: 1, display: 'flex' }}>
                  <Text style={{ width: '33%', fontWeight: 'bold', fontSize: 'medium' }}>
                    {i18n?.t('export.form.result-table-section.header.pathogen')}
                  </Text>
                  <Text style={{ width: '33%', fontWeight: 'bold', fontSize: 'medium' }}>
                    {i18n?.t('export.form.result-table-section.header.probability')}
                  </Text>
                  <Text style={{ width: '34%', fontWeight: 'bold', fontSize: 'medium' }}>
                    {i18n?.t('export.form.result-table-section.header.confirmed')}
                  </Text>
                </Box>
                {Object.entries(strainResults ?? {})
                  .sort(([, a], [, b]) => b - a)
                  .map(([name, prob], index) => (
                    <Box key={name} style={{ flexDirection: 'row' }}>
                      <Text style={{ width: '33%', fontSize: 'medium' }}>{name}</Text>
                      <Text style={{ width: '33%', fontSize: 'medium' }}>{((prob as number) * 100).toFixed(1)}%</Text>
                      <Text style={{ width: '34%', fontSize: 'medium', color: index === 0 ? (allConcordant ? COLORS.STATUS.SUCCESS : COLORS.ACCENT[100]) : undefined }}>
                        {index === 0
                          ? (allConcordant
                              ? '✓'
                              : `⚠ ${i18n?.t('page.analysis-details.verification.discordant') ?? 'Discordant'}`)
                          : ''}
                      </Text>
                    </Box>
                  ))}
              </>
            )}
          </Box>

          {/* Biochemical verification section (strain report only) */}
          {reportType === 'strain' && neededTestsToUse.length > 0 && biochemicalResultsToUse && (
            <div style={{ pageBreakInside: 'avoid', breakInside: 'avoid' }}>
              <SectionHeader header={[i18n?.t('page.analysis-details.verification.title') || 'Vérification biochimique']} />
              <Box style={{ marginHorizontal: 10, padding: 5, paddingTop: 5 }}>
                <Box style={{ flexDirection: 'row', borderBottomWidth: 1, display: 'flex' }}>
                  <Text style={{ width: '30%', fontWeight: 'bold', fontSize: 'medium' }}>Test</Text>
                  <Text style={{ width: '20%', fontWeight: 'bold', fontSize: 'medium' }}>{COL_EXPECTED}</Text>
                  <Text style={{ width: '20%', fontWeight: 'bold', fontSize: 'medium' }}>{COL_RESULT}</Text>
                  <Text style={{ width: '30%', fontWeight: 'bold', fontSize: 'medium' }}>{COL_STATUS}</Text>
                </Box>
                {neededTestsToUse.map((key) => {
                  const rawExpected = (strain as any)?.[key];
                  const entered = biochemicalResultsToUse[key];
                  const concordant = entered != null && entered === rawExpected?.toString();
                  const testLabel = i18n?.t(`properties.${pascalToSnake(key)}`, { defaultValue: key }) ?? key;
                  return (
                    <Box key={key} style={{ flexDirection: 'row' }}>
                      <Text style={{ width: '30%', fontSize: 'medium' }}>{testLabel}</Text>
                      <Text style={{ width: '20%', fontSize: 'medium' }}>{displayTestValue(rawExpected?.toString())}</Text>
                      <Text style={{ width: '20%', fontSize: 'medium' }}>{displayTestValue(entered)}</Text>
                      <Text style={{ width: '30%', fontSize: 'medium', color: concordant ? COLORS.STATUS.SUCCESS : COLORS.ACCENT[100] }}>
                        {concordant
                          ? (i18n?.t('page.analysis-details.verification.concordant') ?? 'Concordant')
                          : (i18n?.t('page.analysis-details.verification.discordant') ?? 'Discordant')}
                      </Text>
                    </Box>
                  );
                })}
              </Box>
            </div>
          )}

          {/* Signature — regulatory lab sign-off; omitted for academic workspaces */}
          {!isAcademic && (
            <Box style={{ flexDirection: 'row', padding: '5px', paddingTop: '40px' }}>
              <Text style={{ width: '50%', fontSize: 'medium' }}>{i18n?.t('export.form.signature-section.authorized-by-manager')}: {AUTHORIZED_BY}</Text>
              <Text style={{ width: '10%', fontSize: 'medium' }}>{i18n?.t('export.form.signature-section.signature')}</Text>
              <Text style={{ width: '40%', fontSize: 'medium', borderBottomWidth: '1px' }}></Text>
            </Box>
          )}
        </VStack>
      </div>
    </>
  );
};
